
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, Paper, Chip,
  IconButton, Tooltip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, InputAdornment, Divider,
  LinearProgress, Grid,
} from "@mui/material";
import {
  Add, Search, Edit, Delete, Visibility, Description,
  CloudUpload, CheckCircle, Done, NavigateBefore,
  Close as CloseIcon, PictureAsPdf, OpenInNew, Person,
} from "@mui/icons-material";
import Share from "@mui/icons-material/Share";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, SHADOW, PAGE_PADDING } from "../../../styles/recruiterTheme";
import SkillRatingInput from "./SkillRatingInput";

const UPLOAD_RESUME = process.env.REACT_APP_RESUME_UPLOAD_AND_SEARCH_API
const BASE         = process.env.REACT_APP_API_BASE_URL;
const BENCH_BASE   = process.env.REACT_APP_API_BENCH_URL;
const PDF_PARSING  = process.env.REACT_APP_API_PDF_PARSING_URL;
const TRACKING_BASE= process.env.REACT_APP_API_TRACKING_URL;
const JOBS_BASE    = process.env.REACT_APP_API_JOBS_URL;

// ─── Shared design tokens (matches CandidateData's purple design system) ───
const BRAND        = "#6750A4";
const BRAND_DARK   = "#4F378B";
const BRAND_DEEP   = "#2D1B5E";
const BRAND_SOFT   = "#8B7DB5";
const BRAND_TINT   = "#F3EEF9";
const BRAND_TINT2  = "#EDE7F6";

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const GradientHeading = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #1A0A3D 0%, #6750A4 50%, #9C7FD4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.25;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCardGrad = styled.div`
  background: ${p => p.gradient || "linear-gradient(135deg,#6750A4,#4F378B)"};
  border-radius: ${RADIUS.md}px;
  padding: 16px;
  min-height: 88px;
  box-sizing: border-box;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px ${p => p.shadow || "rgba(103,80,164,0.26)"};
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px ${p => p.shadow || "rgba(103,80,164,0.36)"}; }
  &::after {
    content: '';
    position: absolute;
    top: -24px; right: -24px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatIconBubble = styled.div`
  width: 40px; height: 40px; border-radius: ${RADIUS.md}px;
  background: rgba(255,255,255,0.16);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  svg { font-size: 20px; color: #fff; }
`;

const StatNumGrad = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLblGrad = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  text-transform: uppercase;
  opacity: 0.88;
  margin-top: 8px;
`;

const SectionLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
  }
`;

// Standalone /bench route: own canvas + PAGE_PADDING. Inside a dashboard tab the
// host already provides the canvas and padding, so this adds none.
const PageRoot = styled.div`
  font-family: ${FONT_BODY};
  width: 100%;
  box-sizing: border-box;
  animation: ${fadeSlideIn} 0.35s ease both;
  ${(p) => (p.$standalone ? `background: #F3EEF9; min-height: 100vh; padding: ${PAGE_PADDING}px;` : "")}
`;

// Page header buttons: one height, same radius/weight.
const headerBtnSx = { textTransform: "none", fontWeight: 600, height: 36, px: 2, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY };

// Bench table cells: same horizontal padding in header and body.
const benchHeadSx = {
  fontWeight: 700, fontSize: TYPE.overline, color: "#8B7DB5", fontFamily: FONT_BODY, textTransform: "uppercase",
  letterSpacing: "0.06em", whiteSpace: "nowrap", borderBottom: "2px solid #F0EAF9", padding: "12px 16px", bgcolor: "#FAF7FF",
};
const BENCH_HEAD_ALIGN = { Experience: "right", Status: "center", Actions: "right" };
// Fixed column widths (sum ≈ 1040px) so the table fits a 1080px content area without scrolling.
const BENCH_COLS = [
  { h: "Person", w: 232 }, { h: "Skills", w: 192 }, { h: "Experience", w: 104 },
  { h: "Availability", w: 128 }, { h: "Last Client", w: 120 }, { h: "Status", w: 120 }, { h: "Actions", w: 144 },
];
const benchIconBtnSx = { width: 28, height: 28, p: 0.5 };

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const handle = async (res) => {
  const data = await res.json();
  if (!res.ok) throw data;
  return data;
};

const getAllBench       = (p = {}) => {
  const qs = new URLSearchParams(p).toString();
  return fetch(`${BENCH_BASE}/${qs ? "?" + qs : ""}`, { headers: getHeaders() }).then(handle);
};
const createBench      = (pl) => fetch(`${BENCH_BASE}/`, { method: "POST", headers: getHeaders(), body: JSON.stringify(pl) }).then(handle);
const updateBench      = (id, pl) => fetch(`${BENCH_BASE}/${id}`, { method: "PUT", headers: getHeaders(), body: JSON.stringify(pl) }).then(handle);
const deleteBench      = (id) => fetch(`${BENCH_BASE}/${id}`, { method: "DELETE", headers: getHeaders() }).then(handle);
const parsePdf         = (file_b64, file_name) =>
  fetch(`${PDF_PARSING}`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ file_b64, file_name }) }).then(handle);
const uploadFile       = (id, file_b64) =>
  fetch(`${BENCH_BASE}/${id}/upload-file`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ file_b64 }) }).then(handle);
const getTrackingByBench = (bench_id) =>
  fetch(`${TRACKING_BASE}/by-resume/${bench_id}`, { headers: getHeaders() }).then(handle);
const createTracking   = (pl) =>
  fetch(`${TRACKING_BASE}/`, { method: "POST", headers: getHeaders(), body: JSON.stringify(pl) }).then(handle);
const getAllJobs        = () => fetch(`${JOBS_BASE}/`, { headers: getHeaders() }).then(handle);

// ✅ FIX 2 — removed trailing slash before query string: /jd/ → /jd
const getAllJds = (p = {}) => {
  const qs = new URLSearchParams(p).toString();
  // return fetch(`${JOBS_BASE}/jd${qs ? "?" + qs : ""}`, { headers: getHeaders() }).then(handle);
  // AFTER
return fetch(`${JOBS_BASE}/jd/${qs ? "?" + qs : ""}`, { headers: getHeaders() }).then(handle);
};

const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result.split(",")[1]);
  r.onerror = () => rej(new Error("Read failed"));
  r.readAsDataURL(file);
});

// ── Resume upload & search helpers ──────────────────────────────────────────
const fetchBenchResumeAsFile = async (personId, fileName = "resume.pdf") => {
  const res = await fetch(`${BASE}/bench/${personId}/file`, { headers: getHeaders() });
  if (!res.ok) throw new Error("Could not fetch resume file");
  const blob = await res.blob();
  return new File([blob], fileName, { type: "application/pdf" });
};

const uploadResumeAndSearch = (formData) =>
  fetch(`${UPLOAD_RESUME}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }, // no Content-Type — browser sets multipart boundary
    body: formData,
  }).then(handle);

// ── Constants ─────────────────────────────────────────────────────────────────
const BENCH_STATUSES   = ["Available", "In Interview", "Deployed", "On Hold", "Resigned"];
const EMPLOYMENT_TYPES = ["Permanent", "Contract", "C2H", "Freelance"];
const NOTICES          = ["Immediate", "15 days", "30 days", "60 days", "90 days"];
const PIPELINE_STAGES  = [
  "Screening", "Technical Round 1", "Technical Round 2", "HR Round",
  "Manager Round", "Final Round", "Offer Stage", "Negotiation",
  "Offer Accepted", "Offer Declined", "Joined", "Rejected", "Withdrawn",
];
const STATUS_COLOR = {
  Available: "success", "In Interview": "warning",
  Deployed: "info", "On Hold": "default", Resigned: "error",
};
const STAGE_COLOR = {
  Screening: "default", "Technical Round 1": "info", "Technical Round 2": "info",
  "HR Round": "primary", "Manager Round": "primary", "Final Round": "primary",
  "Offer Stage": "warning", Negotiation: "warning",
  "Offer Accepted": "success", Joined: "success",
  "Offer Declined": "error", Rejected: "error", Withdrawn: "error",
};
const SCORE_LABEL = ["", "Poor", "Below Avg", "Average", "Good", "Excellent"];
const EMPTY_FORM = {
  name: "", email: "", phone: "", current_role: "", skills: [],
  experience: "", location: "",
  notice_period: "Immediate", last_client: "", last_project: "",
  status: "Available", added_by: "", employment_type: "Permanent", notes: "",
};

const fmtSalary = (v) => {
  if (!v) return "—";
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  return `₹${Number(v).toLocaleString("en-IN")}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};
const nameInitials = (name = "") =>
  name.split(" ").filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";

// ── Stat Card (gradient tile, matches CandidateData's StatCard language) ─────
const StatCard = ({ title, value, color, icon }) => (
  <StatCardGrad gradient={`linear-gradient(135deg, ${color}, ${BRAND_DARK})`} shadow={`${color}44`}>
    <Box sx={{ minWidth: 0 }}>
      <StatNumGrad>{value}</StatNumGrad>
      <StatLblGrad>{title}</StatLblGrad>
    </Box>
    <StatIconBubble>{icon}</StatIconBubble>
  </StatCardGrad>
);

// ── Upload Zone ───────────────────────────────────────────────────────────────
const UploadZone = ({ onFiles, fileRef }) => {
  const [drag, setDrag] = useState(false);
  return (
    <Box
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); onFiles({ target: { files: e.dataTransfer.files } }); }}
      onClick={() => fileRef.current?.click()}
      sx={{
        border: drag ? "2px dashed #6750A4" : "2px dashed #C4B0E8",
        borderRadius: 3, bgcolor: drag ? "#EDE7F6" : "#F8F4FF",
        p: 2.5, display: "flex", alignItems: "center", gap: 2.5,
        cursor: "pointer", transition: "all 0.2s",
        "&:hover": { bgcolor: "#EDE7F6", borderColor: "#6750A4" },
      }}>
      <Avatar sx={{ width: 52, height: 52, bgcolor: drag ? "#6750A4" : "#EDE7F6", flexShrink: 0 }}>
        <CloudUpload sx={{ fontSize: 28, color: drag ? "#fff" : "#6750A4" }} />
      </Avatar>
      <Box flex={1}>
        <Typography fontWeight={700} color="#6750A4" fontSize={14} fontFamily={FONT_BODY}>
          Drag &amp; drop PDF resumes here · AI will auto-extract details
        </Typography>
        <Typography fontSize={12} color="text.secondary" mt={0.5} fontFamily={FONT_BODY}>
          Multiple files supported · PDF only · Original file stored
        </Typography>
      </Box>
      <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple hidden onChange={onFiles} />
    </Box>
  );
};

// ── Parse Progress ────────────────────────────────────────────────────────────
const ParseProgress = ({ files, onReview, onClear }) => {
  const allDone = files.every(f => f.status !== "parsing" && f.status !== "pending");
  return (
    <Card variant="outlined" sx={{ borderColor: "#C4B0E8", borderRadius: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            {!allDone && <CircularProgress size={14} />}
            <Typography fontWeight={700} fontSize={14} fontFamily={FONT_BODY}>
              {allDone
                ? `Parsing complete — ${files.filter(f => f.status === "done").length} parsed`
                : `Parsing ${files.length} resume(s) with AI…`}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            {allDone && (
              <Button variant="contained" size="small" onClick={onReview} startIcon={<Done />}
                sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>
                Review &amp; Save
              </Button>
            )}
            <IconButton size="small" onClick={onClear}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
        {files.map((entry, i) => (
          <Box key={i} display="flex" alignItems="center" gap={1.5}
            sx={{ p: 1, bgcolor: "#FAF7FF", borderRadius: 1.5, mb: 0.8 }}>
            <Description fontSize="small" color="action" />
            <Box flex={1} minWidth={0}>
              <Typography fontSize={12} fontWeight={600} noWrap fontFamily={FONT_BODY}>{entry.file.name}</Typography>
              {entry.status === "parsing" && <LinearProgress sx={{ mt: 0.5, height: 3, borderRadius: 2 }} />}
            </Box>
            {entry.status === "pending" && <Chip label="Waiting"       size="small" sx={{ fontSize: 11 }} />}
            {entry.status === "parsing" && <CircularProgress size={16} />}
            {entry.status === "done"    && <Chip label="Parsed ✓"      size="small" color="success" sx={{ fontSize: 11 }} />}
            {entry.status === "error"   && <Chip label="Fill manually" size="small" color="warning" sx={{ fontSize: 11 }} />}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

// ── PDF Viewer ────────────────────────────────────────────────────────────────
const PdfViewer = ({ open, onClose, person }) => {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [fetching, setFetching] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (!open || !person?._id || !person?.resume_file) return;
    let url = null;
    setFetching(true); setErr("");
    fetch(`${BASE}/bench/${person._id}/file`, { headers: getHeaders() })
      .then(res => { if (!res.ok) throw new Error(`${res.status}`); return res.blob(); })
      .then(blob => { url = URL.createObjectURL(blob); setBlobUrl(url); })
      .catch(e => setErr(e.message))
      .finally(() => setFetching(false));
    return () => { if (url) URL.revokeObjectURL(url); setBlobUrl(null); setErr(""); };
  }, [open, person?._id, person?.resume_file]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: "92vh", display: "flex", flexDirection: "column", borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #EDE7F6", py: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <PictureAsPdf color="error" />
            <Typography fontWeight={700} fontFamily={FONT_DISPLAY} color={BRAND_DEEP}>{person?.name} — Resume</Typography>
          </Box>
          <Box display="flex" gap={1}>
            {blobUrl && (
              <Tooltip title="Download">
                <IconButton size="small" onClick={() => {
                  const a = document.createElement("a"); a.href = blobUrl;
                  a.download = `${person?.name}_resume.pdf`; a.click();
                }}>
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, flex: 1, overflow: "hidden" }}>
        {fetching && <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>}
        {!fetching && err && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={2}>
            <PictureAsPdf sx={{ fontSize: 64, color: "#ef9a9a" }} />
            <Typography color="error">{err}</Typography>
          </Box>
        )}
        {!fetching && blobUrl && <iframe src={blobUrl} title="Resume" style={{ width: "100%", height: "100%", border: "none" }} />}
      </DialogContent>
    </Dialog>
  );
};

// ── Bench Detail Content ──────────────────────────────────────────────────────
function BenchDetailContent({ person, jobs, allJds = [], onClose, onEdit, onViewPdf }) {
  const [tracking,      setTracking]      = React.useState([]);
  const [loadingT,      setLoadingT]      = React.useState(true);
  const [tab,           setTab]           = React.useState(0);
  const [addPipeline,   setAddPipeline]   = React.useState(false);
  const [pipeForm,      setPipeForm]      = React.useState({
    job_id: "", current_stage: "Screening", recruiter: "", notes: "", next_step: "",
  });
  const [pipeError,     setPipeError]     = React.useState("");
  const [pipeSaving,    setPipeSaving]    = React.useState(false);
  const [assignOpen,    setAssignOpen]    = React.useState(false);
  const [assignForm,    setAssignForm]    = React.useState({
    job_id: "", senior_reviewer_email: "", senior_reviewer_name: "",
  });
  const [assignSaving,  setAssignSaving]  = React.useState(false);
  const [assignError,   setAssignError]   = React.useState("");
  const [assignDone,    setAssignDone]    = React.useState(false);
  const [jdReviews,     setJdReviews]     = React.useState([]);
  const [loadingJD,     setLoadingJD]     = React.useState(false);
  const [reviewPdfUrl,  setReviewPdfUrl]  = React.useState(null);
  const [reviewPdfOpen, setReviewPdfOpen] = React.useState(false);
  const [attachOpen, setAttachOpen]       = React.useState(false);
  const [attachMode, setAttachMode]       = React.useState(null); 



  const loadTracking = React.useCallback(() => {
    setLoadingT(true);
    getTrackingByBench(person.bench_id)
      .then(res => setTracking(res.data || []))
      .catch(() => setTracking([]))
      .finally(() => setLoadingT(false));
  }, [person.bench_id]);

  const loadJdReviews = React.useCallback(() => {
    setLoadingJD(true);
    fetch(`${BASE}/jd-review/?bench_id=${person.bench_id}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setJdReviews(d.data || []))
      .catch(() => setJdReviews([]))
      .finally(() => setLoadingJD(false));
  }, [person.bench_id]);

  React.useEffect(() => { loadTracking(); loadJdReviews(); }, [loadTracking, loadJdReviews]);

  const activeTrack = tracking[0];

  const openReviewPdf = (reviewId) => {
    fetch(`${BASE}/jd-review/${reviewId}/file`, { headers: getHeaders() })
      .then(r => r.blob())
      .then(b => { setReviewPdfUrl(URL.createObjectURL(b)); setReviewPdfOpen(true); })
      .catch(() => alert("Could not load PDF"));
  };

  const handleAddPipeline = async () => {
    if (!pipeForm.job_id) { setPipeError("Please select a job"); return; }
    setPipeSaving(true); setPipeError("");
    try {
      let jobId = pipeForm.job_id;
      let jobTitle = "";
      let clientName = "";
      let source = "zentree";
      let jdDescription = "";

      try {
        const parsed = JSON.parse(pipeForm.job_id);
        jobId  = parsed.id;
        source = parsed.source;
        const rbJob = allJds.find(j => j._id === jobId);
        jobTitle      = rbJob?.jobRole || rbJob?.jobTitle || "";
        clientName    = rbJob?.companyName || "";
        jdDescription = rbJob?.job_description || rbJob?.jobDescription || "";
      } catch {
        const job = jobs.find(j => j._id === jobId);
        jobTitle   = job?.title || "";
        clientName = job?.client_name || "";
      }
      const promoteRes = await fetch(`${BENCH_BASE}/${person.bench_id}/promote-to-candidate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ job_id: jobId, job_title: jobTitle, client_name: clientName, source }),
      }).then(r => r.json());
    if (person.resume_file) {
      try {
        const resumeFile = await fetchBenchResumeAsFile(person._id, person.resume_file);
        let currentUser = {};
        try { currentUser = JSON.parse(localStorage.getItem("rbotUser") || localStorage.getItem("user") || "{}") || {}; } catch { /* keep {} */ }

        const fd = new FormData();
        fd.append("jd._id", promoteRes.jd_object_id || "");
        fd.append("job_description", jdDescription);
        fd.append("skills", Array.isArray(person.skills)
          ? person.skills.map(s => (typeof s === "object" ? s.name : s)).join(", ")
          : (person.skills || ""));
        fd.append("experience", person.experience || "");
        fd.append("jdID", promoteRes.jdID || "");
        fd.append("job_role", jobTitle);
        fd.append("recruiter_email", currentUser.email || "");
        fd.append("recruiterid", promoteRes.recruiterid || "");
        fd.append("files", resumeFile);
        console.log("formdataa",fd)

        await uploadResumeAndSearch(fd);
      } catch (searchErr) {
        console.warn("Resume upload & search skipped:", searchErr.message);
      }
    }

      await createTracking({
        resume_id:       person.bench_id?.trim(),
        candidate_name:  person.name,
        job_id:          jobId,
        job_title:       jobTitle,
        client_name:     clientName,
        current_stage:   pipeForm.current_stage,
        recruiter:       pipeForm.recruiter,
        notes:           pipeForm.notes,
        next_step:       pipeForm.next_step,
        pipeline_status: "Active",
      });

      setAddPipeline(false);
      setPipeForm({ job_id: "", current_stage: "Screening", recruiter: "", notes: "", next_step: "" });
      loadTracking();
    } catch (err) {
      setPipeError(err?.message || "Failed to add to pipeline");
    } finally { setPipeSaving(false); }
  };

  const handleAssign = async () => {
    if (!assignForm.job_id || !assignForm.senior_reviewer_email) {
      setAssignError("Job and reviewer email are required"); return;
    }
    setAssignSaving(true); setAssignError("");
    try {
      let jobId  = assignForm.job_id;
      let source = "zentree";
      try {
        const parsed = JSON.parse(assignForm.job_id);
        jobId  = parsed.id;
        source = parsed.source;
      } catch { /* plain string fallback */ }

      // ✅ FIX 1b — was localStorage.getItem("access_token"), must be "token"
      const res = await fetch(`${BASE}/jd-review/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          bench_id:              person.bench_id,
          job_id:                jobId,
          source,
          senior_reviewer_email: assignForm.senior_reviewer_email,
          senior_reviewer_name:  assignForm.senior_reviewer_name,
          assigned_by:           JSON.parse(localStorage.getItem("user") || "{}").email || "",
        }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message);
      setAssignDone(true);
    } catch (e) {
      setAssignError(e.message);
    } finally { setAssignSaving(false); }
  };

  return (
    <>
      <DialogContent sx={{ p: 0 }}>
        {/* ── Header ── */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #EDE7F6" }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: BRAND, fontSize: 20, fontWeight: 700, fontFamily: FONT_BODY }}>
              {nameInitials(person.name)}
            </Avatar>
            <Box flex={1}>
              <GradientHeading variant="h5" sx={{ fontSize: `${TYPE.section}px !important` }}>{person.name}</GradientHeading>
              <Typography color="text.secondary" fontSize={13} fontFamily={FONT_BODY} fontWeight={600}>{person.current_role}</Typography>
              <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                <Chip label={person.status} color={STATUS_COLOR[person.status] || "default"} size="small" sx={{ fontWeight: 700 }} />
                <Chip label={person.employment_type} size="small" variant="outlined" />
                {activeTrack && (
                  <Chip
                    label={`Pipeline: ${activeTrack.current_stage}`}
                    color={STAGE_COLOR[activeTrack.current_stage] || "default"}
                    size="small" variant="outlined" sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box display="flex" mt={2}>
            {["Profile & Resume", "Pipeline"].map((label, i) => (
              <Box key={i} onClick={() => { setTab(i); setAddPipeline(false); }} sx={{
                px: 2, py: 1, cursor: "pointer",
                fontFamily: FONT_BODY,
                fontWeight: tab === i ? 700 : 400, fontSize: 13,
                borderBottom: tab === i ? `2px solid ${BRAND}` : "2px solid transparent",
                color: tab === i ? BRAND : "text.secondary",
                transition: "all 0.15s",
              }}>{label}</Box>
            ))}
          </Box>
        </Box>

        {/* ── TAB 0 — Profile ── */}
        {tab === 0 && (
          <Box p={3}>
            <Grid container spacing={2} mb={2}>
              {[
                ["Email",           person.email],
                ["Phone",           person.phone || "—"],
                ["Location",        person.location || "—"],
                ["Experience",      `${person.experience} years`],
                // ["Current Salary",  fmtSalary(person.current_salary)],
                // ["Expected Salary", fmtSalary(person.expected_salary)],
                ["Notice Period",   person.notice_period || "—"],
                ["Availability",    fmtDate(person.availability_date)],
                ["Bench Since",     fmtDate(person.bench_since)],
                ["Added By",        person.added_by || "—"],
                ["Last Client",     person.last_client || "—"],
                ["Last Project",    person.last_project || "—"],
              ].map(([label, val]) => (
                <Grid item xs={6} sm={4} key={label}>
                  <Typography fontSize={11} color="#8B7DB5" fontWeight={700} textTransform="uppercase" fontFamily={FONT_BODY} letterSpacing="0.06em">{label}</Typography>
                  <Typography fontWeight={600} fontSize={13} fontFamily={FONT_BODY} color="#2D1B5E">{val}</Typography>
                </Grid>
              ))}
            </Grid>

            {person.skills?.length > 0 && (
              <Box mb={2}>
                <Typography fontSize={11} color="#8B7DB5" fontWeight={700} textTransform="uppercase" mb={1} fontFamily={FONT_BODY} letterSpacing="0.06em">Skills</Typography>
                <Box display="flex" flexWrap="wrap" gap={0.8}>
                  {(Array.isArray(person.skills) ? person.skills : []).map((s, i) => {
                    const skill = typeof s === "object" ? s : { name: s, rating: 0 };
                    const ratingColor = r => r <= 1 ? "#ef4444" : r === 2 ? "#f97316" : r === 3 ? "#eab308" : r === 4 ? "#22c55e" : "#6750A4";
                    return (
                      <Box key={i} display="flex" alignItems="center" gap={1}
                        sx={{ px: 1.5, py: 0.8, border: "1px solid #EDE7F6", borderRadius: 2, bgcolor: "#FAF7FF" }}>
                        <Typography fontSize={12} fontWeight={600} flex={1} fontFamily={FONT_BODY}>{skill.name}</Typography>
                        {skill.rating > 0 && (
                          <Box display="flex" gap={0.4}>
                            {/* {[1, 2, 3, 4, 5].map(r => (
                              <Box key={r} sx={{ width: 8, height: 8, borderRadius: "50%",
                                bgcolor: r <= skill.rating ? ratingColor(skill.rating) : "#e2e8f0" }} />
                            ))} */}
                            {[1, 2, 3, 4, 5].map(r => (
                              <Box key={r} sx={{
                                fontSize: 13,
                                color: r <= skill.rating ? ratingColor(skill.rating) : "#e2e8f0",
                                lineHeight: 1,
                              }}>★</Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {person.notes && (
              <Box p={1.5} bgcolor="#FAF7FF" borderRadius={2} mb={2} sx={{ border: "1px solid #EDE7F6" }}>
                <Typography fontSize={11} color="#8B7DB5" fontWeight={700} textTransform="uppercase" mb={0.5} fontFamily={FONT_BODY} letterSpacing="0.06em">Notes</Typography>
                <Typography fontSize={13} fontFamily={FONT_BODY} color="#2D1B5E">{person.notes}</Typography>
              </Box>
            )}

            <Box p={2} borderRadius={2} display="flex" alignItems="center" gap={2}
              sx={{ bgcolor: "#FAF7FF", border: "1px solid #EDE7F6" }}>
              <Box flex={1}>
                <Typography fontWeight={700} fontSize={13} fontFamily={FONT_BODY} color={person.resume_file ? "success.dark" : "text.secondary"}>
                  {person.resume_file ? "Resume PDF available" : "No resume file uploaded"}
                </Typography>
                <Typography fontSize={11} color="text.secondary" fontFamily={FONT_BODY}>
                  {person.resume_file ? `File: ${person.resume_file}` : "Upload via drag-and-drop"}
                </Typography>
              </Box>
              {person.resume_file && (
                <Button variant="contained" size="small" onClick={onViewPdf}
                  sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>
                  View PDF
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* ── TAB 1 — Pipeline ── */}
        {tab === 1 && (
          <Box p={3}>

            {/* JD Review Section */}
            {loadingJD ? (
              <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
            ) : jdReviews.length > 0 && (
              <Box mb={3}>
                <Typography fontSize={11} fontWeight={700} color="#8B7DB5" textTransform="uppercase" mb={1.5} fontFamily={FONT_BODY} letterSpacing="0.06em">
                  JD Resume Reviews
                </Typography>
                {jdReviews.map((review) => {
                  const statusColor = {
                    "Accepted":       { bg: "#f0fdf4", border: "#86efac", chip: "success", dot: "#15803d" },
                    "Rejected":       { bg: "#fef2f2", border: "#fca5a5", chip: "error",   dot: "#dc2626" },
                    "Pending Review": { bg: "#fffbeb", border: "#fcd34d", chip: "warning", dot: "#d97706" },
                    "Pending Upload": { bg: "#F8F4FF", border: "#C4B0E8", chip: "info",    dot: "#6750A4" },
                  }[review.status] || { bg: "#FAF7FF", border: "#EDE7F6", chip: "default", dot: "#64748b" };

                  return (
                    <Box key={review._id} p={2} mb={1.5} borderRadius={2}
                      sx={{ bgcolor: statusColor.bg, border: `1px solid ${statusColor.border}` }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={0.4}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: statusColor.dot, flexShrink: 0 }} />
                            <Typography fontWeight={700} fontSize={13} fontFamily={FONT_BODY}>{review.job_title}</Typography>
                            <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>@ {review.jdID}</Typography>
                            <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>@ {review.client_name}</Typography>
                          </Box>
                          <Typography fontSize={11} color="text.secondary" fontFamily={FONT_BODY}>
                            Reviewer: <strong>{review.senior_reviewer_name}</strong>
                            {" · "}Assigned by: <strong>{review.assigned_by}</strong>
                          </Typography>
                        </Box>
                        <Chip label={review.status} color={statusColor.chip} size="small" sx={{ fontWeight: 700, fontSize: 11 }} />
                      </Box>

                      {review.status === "Rejected" && review.feedback && (
                        <Box p={1.2} bgcolor="#fef2f2" borderRadius={1.5} mb={1} sx={{ borderLeft: "3px solid #ef4444" }}>
                          <Typography fontSize={11} fontWeight={700} color="#dc2626" textTransform="uppercase" mb={0.5} fontFamily={FONT_BODY}>Reviewer Feedback</Typography>
                          <Typography fontSize={12} color="#7f1d1d" fontFamily={FONT_BODY}>{review.feedback}</Typography>
                        </Box>
                      )}

                      {review.status === "Accepted" && (
                        <Box display="flex" alignItems="center" gap={1.5} mt={1} p={1.5}
                          bgcolor="#dcfce7" borderRadius={1.5} sx={{ border: "1px solid #86efac" }}>
                          <CheckCircle sx={{ color: "#15803d", fontSize: 20 }} />
                          <Box flex={1}>
                            <Typography fontSize={12} fontWeight={700} color="#15803d" fontFamily={FONT_BODY}>Tailored resume approved — ready to process!</Typography>
                            <Typography fontSize={11} color="#166534" fontFamily={FONT_BODY}>You can now view the resume and add this candidate to the pipeline.</Typography>
                          </Box>
                          <Box display="flex" gap={1}>
                            <Button size="small" variant="outlined"
                              onClick={() => openReviewPdf(review._id)}
                              sx={{ fontSize: 11, borderColor: "#15803d", color: "#15803d", "&:hover": { bgcolor: "#f0fdf4" } }}
                              startIcon={<PictureAsPdf sx={{ fontSize: 14 }} />}>
                              View Resume
                            </Button>
                            <Button size="small" variant="contained"
                              onClick={() => {
                                const jobVal = review.source === "resourcing_bot"
                                  ? JSON.stringify({ id: review.job_id, source: "resourcing_bot" })
                                  : review.job_id;
                                setPipeForm(p => ({ ...p, job_id: jobVal }));
                                setAttachMode("direct"); 
                                // setAddPipeline(true);
                                setAttachOpen(true); 
                              }}
                              sx={{ fontSize: 11, bgcolor: "#15803d", "&:hover": { bgcolor: "#166534" } }}>
                              + Add to Pipeline
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {review.status === "Pending Upload" && (
                        <Typography fontSize={11} color="#6750A4" mt={0.5} fontFamily={FONT_BODY}>
                          ⏳ Waiting for candidate to upload their tailored resume…
                        </Typography>
                      )}
                      {review.status === "Pending Review" && (
                        <Typography fontSize={11} color="#d97706" mt={0.5} fontFamily={FONT_BODY}>
                          ⏳ Resume uploaded — waiting for {review.senior_reviewer_name} to review…
                        </Typography>
                      )}
                      {review.rejection_count > 0 && (
                        <Typography fontSize={11} color="text.disabled" mt={0.5} fontFamily={FONT_BODY}>
                          Rejected {review.rejection_count} time{review.rejection_count > 1 ? "s" : ""}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* PDF viewer for tailored resume */}
            <Dialog open={reviewPdfOpen} onClose={() => { setReviewPdfOpen(false); setReviewPdfUrl(null); }}
              maxWidth="lg" fullWidth PaperProps={{ sx: { height: "90vh", borderRadius: "16px" } }}>
              <DialogTitle sx={{ py: 1.5, borderBottom: "1px solid #EDE7F6" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={700} fontFamily={FONT_DISPLAY} color={BRAND_DEEP}>Tailored Resume — {person.name}</Typography>
                  <IconButton size="small" onClick={() => { setReviewPdfOpen(false); setReviewPdfUrl(null); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                {reviewPdfUrl && <iframe src={reviewPdfUrl} title="Tailored Resume"
                  style={{ width: "100%", height: "100%", border: "none" }} />}
              </DialogContent>
            </Dialog>

            {/* Add to Pipeline form */}
            {addPipeline ? (
              <Box mb={3} p={2.5} borderRadius={2} sx={{ border: `1.5px solid ${BRAND}`, bgcolor: "#F8F4FF" }}>
                <SectionLabel fontSize={14} mb={2}>Add to Pipeline</SectionLabel>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField select fullWidth size="small" required label="Select Job" name="job_id"
                      value={pipeForm.job_id} onChange={e => setPipeForm(p => ({ ...p, job_id: e.target.value }))}>
                      <MenuItem value="">— Select a job —</MenuItem>
                      {allJds.length > 0 && (
                        <MenuItem disabled sx={{ fontSize: 11, fontWeight: 700, color: "#7c3aed",
                          bgcolor: "#faf5ff", opacity: "1 !important", pointerEvents: "none" }}>
                          ── Active JDs ──
                        </MenuItem>
                      )}
                      {allJds.map(j => (
                        <MenuItem key={`rb-${j._id}`} value={JSON.stringify({ id: j._id, source: "resourcing_bot" })}>
                          <Box>
                            <Typography fontSize={13} fontWeight={600}>
                              {j.jobRole || j.jobTitle}
                              {" "}<Box component="span" sx={{ color: "#0277bd", fontWeight: 700 }}>({j.jdID})</Box>
                            </Typography>
                            <Typography fontSize={11} color="text.secondary">
                              {j.companyName}
                              <Chip label="RB" size="small" sx={{ ml: 1, height: 16, fontSize: 11, bgcolor: "#ede9fe", color: "#7c3aed" }} />
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Starting Stage" name="current_stage"
                      value={pipeForm.current_stage} onChange={e => setPipeForm(p => ({ ...p, current_stage: e.target.value }))}>
                      {PIPELINE_STAGES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Recruiter" value={pipeForm.recruiter}
                      onChange={e => setPipeForm(p => ({ ...p, recruiter: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Next Step" value={pipeForm.next_step}
                      onChange={e => setPipeForm(p => ({ ...p, next_step: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} size="small" label="Notes" value={pipeForm.notes}
                      onChange={e => setPipeForm(p => ({ ...p, notes: e.target.value }))} />
                  </Grid>
                </Grid>
                {pipeError && <Alert severity="error" sx={{ mt: 1.5 }}>{pipeError}</Alert>}
                <Box display="flex" gap={1} mt={2} justifyContent="flex-end">
                  <Button size="small" onClick={() => { setAddPipeline(false); setPipeError(""); }}
                    sx={{ textTransform: "none", color: "#64748b" }}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleAddPipeline}
                    disabled={pipeSaving || !pipeForm.job_id}
                    sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}
                    endIcon={pipeSaving ? <CircularProgress size={14} color="inherit" /> : null}>
                    {pipeSaving ? "Adding…" : "Add to Pipeline"}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button size="small" variant="outlined" onClick={() => setAddPipeline(true)}
                  sx={{ textTransform: "none", fontWeight: 700, borderColor: BRAND, color: BRAND }}>
                  + Add to Pipeline
                </Button>
              </Box>
            )}

            {/* Pipeline records */}
            {loadingT ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>
            ) : tracking.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
                <Typography color="text.secondary" fontWeight={600} fontFamily={FONT_BODY}>No pipeline records found</Typography>
                <Typography fontSize={13} color="text.disabled" fontFamily={FONT_BODY}>Click "Add to Pipeline" to start tracking.</Typography>
              </Box>
            ) : tracking.map((track, tIdx) => (
              <Box key={track._id} mb={tIdx < tracking.length - 1 ? 4 : 0}>
                <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                  <Chip label={track.current_stage} color={STAGE_COLOR[track.current_stage] || "default"} size="small" sx={{ fontWeight: 700 }} />
                  <Chip label={track.pipeline_status} size="small" variant="outlined" />
                  {track.recruiter   && <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>Recruiter: <strong>{track.recruiter}</strong></Typography>}
                  {track.job_title   && <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>Job: <strong>{track.job_title}</strong></Typography>}
                  {track.client_name && <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>Client: <strong>{track.client_name}</strong></Typography>}
                </Box>

                {track.stage_history?.length > 0 && (
                  <Box mb={3}>
                    <Typography fontSize={11} color="#8B7DB5" fontWeight={700} textTransform="uppercase" mb={1.5} fontFamily={FONT_BODY} letterSpacing="0.06em">Stage History</Typography>
                    {track.stage_history.map((entry, i) => (
                      <Box key={i} display="flex" gap={1.5} alignItems="flex-start">
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ pt: 0.3 }}>
                          <Box sx={{
                            width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                            bgcolor: i === track.stage_history.length - 1 ? BRAND : "#C4B0E8",
                            border: "2px solid", borderColor: i === track.stage_history.length - 1 ? BRAND : "#EDE7F6",
                          }} />
                          {i < track.stage_history.length - 1 && (
                            <Box sx={{ width: 2, flexGrow: 1, minHeight: 20, bgcolor: "#EDE7F6", my: 0.3 }} />
                          )}
                        </Box>
                        <Box pb={1.5}>
                          <Typography fontWeight={600} fontSize={13} fontFamily={FONT_BODY}>{entry.stage}</Typography>
                          <Typography fontSize={11} color="text.secondary" fontFamily={FONT_BODY}>
                            {fmtDate(entry.entered_at)}{entry.exited_at ? ` → ${fmtDate(entry.exited_at)}` : " · current"}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {track.interviews?.length > 0 && (
                  <Box mb={3}>
                    <Typography fontSize={11} color="#8B7DB5" fontWeight={700} textTransform="uppercase" mb={1.5} fontFamily={FONT_BODY} letterSpacing="0.06em">
                      Interviews ({track.interviews.length})
                    </Typography>
                    {track.interviews.map((iv, i) => (
                      <Box key={i} p={2} borderRadius={2} mb={1.5} sx={{ border: "1px solid #EDE7F6", bgcolor: "#FAFAFA" }}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Box>
                            <Typography fontWeight={700} fontSize={13} fontFamily={FONT_BODY}>Round {i + 1} — {iv.interview_type}</Typography>
                            <Typography fontSize={11} color="text.secondary" fontFamily={FONT_BODY}>{iv.interviewer} · {fmtDate(iv.interview_date)}</Typography>
                          </Box>
                          <Box display="flex" gap={0.4} alignItems="center">
                            {/* {[1, 2, 3, 4, 5].map(s => (
                              <Box key={s} sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: s <= (iv.feedback_score || 0) ? "#6750A4" : "#e0e0e0" }} />
                            ))} */}
                            {[1, 2, 3, 4, 5].map(s => (
                              <Box key={s} sx={{
                                fontSize: 16,
                                color: s <= (iv.feedback_score || 0) ? "#f59e0b" : "#e2e8f0",
                                lineHeight: 1,
                              }}>★</Box>
                            ))}
                            <Typography fontSize={11} fontWeight={700} color={BRAND} ml={0.5} fontFamily={FONT_BODY}>{SCORE_LABEL[iv.feedback_score] || "—"}</Typography>
                          </Box>
                        </Box>
                        {iv.feedback_summary && <Typography fontSize={12} mb={1} fontFamily={FONT_BODY}>{iv.feedback_summary}</Typography>}
                        <Box display="flex" gap={2}>
                          {iv.strengths?.length > 0 && (
                            <Box flex={1}>
                              <Typography fontSize={11} fontWeight={700} color="#2e7d32" textTransform="uppercase" mb={0.5} fontFamily={FONT_BODY}>Strengths</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.4}>
                                {iv.strengths.map((s, si) => <Chip key={si} label={s} size="small" sx={{ fontSize: 11, height: 20, bgcolor: "#e8f5e9", color: "#1b5e20" }} />)}
                              </Box>
                            </Box>
                          )}
                          {iv.weaknesses?.length > 0 && (
                            <Box flex={1}>
                              <Typography fontSize={11} fontWeight={700} color="#c62828" textTransform="uppercase" mb={0.5} fontFamily={FONT_BODY}>Areas to Improve</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.4}>
                                {iv.weaknesses.map((w, wi) => <Chip key={wi} label={w} size="small" sx={{ fontSize: 11, height: 20, bgcolor: "#ffebee", color: "#b71c1c" }} />)}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {track.next_step && (
                  <Box p={1.5} bgcolor="#fffde7" borderRadius={2} sx={{ border: "1px solid #fff176" }}>
                    <Typography fontSize={11} fontWeight={600} color="#f57f17" textTransform="uppercase" fontFamily={FONT_BODY}>Next Step</Typography>
                    <Typography fontSize={13} fontFamily={FONT_BODY}>{track.next_step}</Typography>
                  </Box>
                )}

                {tIdx < tracking.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* ── Footer actions ── */}
      <DialogActions sx={{ px: 3, pb: 2.5, borderTop: "1px solid #EDE7F6", justifyContent: "space-between" }}>
        {/* <Button size="small" variant="outlined" onClick={() => setAssignOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700, borderColor: "#075985", color: "#075985",
            "&:hover": { bgcolor: "#f0f9ff" } }}>
          Send JD to Candidate
        </Button> */}
<Button size="small" variant="contained" onClick={() => { setAttachOpen(true); setAttachMode(null); }}
  sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>
  Attach to Job
</Button>

        <Box display="flex" gap={1}>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" onClick={onEdit}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>
            Edit
          </Button>
        </Box>
      </DialogActions>


<Dialog open={attachOpen} onClose={() => { setAttachOpen(false); setAttachMode(null); setAssignDone(false); setAssignError(""); setPipeError(""); }}
  maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
  <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, color: BRAND_DEEP }}>Attach {person.name} to a Job</DialogTitle>
  <DialogContent sx={{ pt: 2 }}>
    {!attachMode ? (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box onClick={() => setAttachMode("direct")} sx={{
            p: 2.5, height: "100%", border: "1.5px solid #22c55e", borderRadius: 2, cursor: "pointer",
            bgcolor: "#f0fdf4", "&:hover": { bgcolor: "#dcfce7" } }}>
            <Typography fontWeight={700} color="#15803d" fontFamily={FONT_BODY}>Direct Attach</Typography>
            <Typography fontSize={12} color="text.secondary" mt={0.5} fontFamily={FONT_BODY}>
              Skip review — immediately add this candidate to the pipeline for the
              selected job, using their current resume on file.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box onClick={() => setAttachMode("review")} sx={{
            p: 2.5, height: "100%", border: "1.5px solid #7c3aed", borderRadius: 2, cursor: "pointer",
            bgcolor: "#faf5ff", "&:hover": { bgcolor: "#f3e8ff" } }}>
            <Typography fontWeight={700} color="#7c3aed" fontFamily={FONT_BODY}>Send for Review</Typography>
            <Typography fontSize={12} color="text.secondary" mt={0.5} fontFamily={FONT_BODY}>
              Send the JD to the candidate, have them upload a tailored resume,
              and get senior-reviewer sign-off before adding to the pipeline.
            </Typography>
          </Box>
        </Grid>
      </Grid>

    ) : attachMode === "direct" ? (
      /* ── DIRECT ATTACH FORM ───────────────────────────────────────── */
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField select fullWidth size="small" required label="Select Job" name="job_id"
            value={pipeForm.job_id} onChange={e => setPipeForm(p => ({ ...p, job_id: e.target.value }))}>
            <MenuItem value="">— Select a job —</MenuItem>
            {allJds.length > 0 && (
              <MenuItem disabled sx={{ fontSize: 11, fontWeight: 700, color: "#7c3aed",
                bgcolor: "#faf5ff", opacity: "1 !important", pointerEvents: "none" }}>
                ── Active JDs ──
              </MenuItem>
            )}
            {allJds.map(j => (
              <MenuItem key={`rb-${j._id}`} value={JSON.stringify({ id: j._id, source: "resourcing_bot" })}>
                <Box>
                  <Typography fontSize={13} fontWeight={600}>
                    {j.jobRole || j.jobTitle}
                    {" "}<Box component="span" sx={{ color: "#0277bd", fontWeight: 700 }}>({j.jdID})</Box>
                  </Typography>
                  <Typography fontSize={11} color="text.secondary">
                    {j.companyName}
                    <Chip label="RB" size="small" sx={{ ml: 1, height: 16, fontSize: 11, bgcolor: "#ede9fe", color: "#7c3aed" }} />
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth size="small" label="Starting Stage" name="current_stage"
            value={pipeForm.current_stage} onChange={e => setPipeForm(p => ({ ...p, current_stage: e.target.value }))}>
            {PIPELINE_STAGES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Recruiter" value={pipeForm.recruiter}
            onChange={e => setPipeForm(p => ({ ...p, recruiter: e.target.value }))} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Next Step" value={pipeForm.next_step}
            onChange={e => setPipeForm(p => ({ ...p, next_step: e.target.value }))} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth multiline rows={2} size="small" label="Notes" value={pipeForm.notes}
            onChange={e => setPipeForm(p => ({ ...p, notes: e.target.value }))} />
        </Grid>
        {pipeError && <Grid item xs={12}><Alert severity="error">{pipeError}</Alert></Grid>}
      </Grid>

    ) : (
      /* ── SEND FOR REVIEW FORM ─────────────────────────────────────── */
      assignDone ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="success.main" fontWeight={700} fontFamily={FONT_DISPLAY}>✅ Email sent to candidate!</Typography>
          <Typography fontSize={13} color="text.secondary" mt={1} fontFamily={FONT_BODY}>
            They'll receive the JD and a secure upload link.
            You'll be notified once the senior reviewer accepts.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <TextField
              select fullWidth size="small" required label="Select Job (JD)"
              value={assignForm.job_id}
              onChange={e => setAssignForm(p => ({ ...p, job_id: e.target.value }))}
              helperText={allJds.length === 0 ? "No active JDs found — check REACT_APP_API_JOBS_URL env var" : ""}
            >
              <MenuItem value="">— Select —</MenuItem>
              {allJds.length > 0 && (
                <MenuItem disabled sx={{ fontSize: 11, fontWeight: 700, color: "#7c3aed",
                  bgcolor: "#faf5ff", opacity: "1 !important", pointerEvents: "none" }}>
                  ── Active JDs ──
                </MenuItem>
              )}
              {allJds.map(j => (
                <MenuItem key={`rb-${j._id}`} value={JSON.stringify({ id: j._id, source: "resourcing_bot" })}>
                  <Box>
                    <Typography fontSize={13} fontWeight={600}>
                      {j.jobRole || j.jobTitle}{" "}
                      <Box component="span" sx={{ color: "#0277bd", fontWeight: 700 }}>({j.jdID})</Box>
                    </Typography>
                    <Typography fontSize={11} color="text.secondary">{j.companyName}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" required label="Senior Reviewer Name"
              value={assignForm.senior_reviewer_name}
              onChange={e => setAssignForm(p => ({ ...p, senior_reviewer_name: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" required label="Senior Reviewer Email"
              type="email" value={assignForm.senior_reviewer_email}
              onChange={e => setAssignForm(p => ({ ...p, senior_reviewer_email: e.target.value }))} />
          </Grid>
          {assignError && <Grid item xs={12}><Alert severity="error">{assignError}</Alert></Grid>}
        </Grid>
      )
    )}
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2.5 }}>
    {attachMode && !assignDone && (
      <Button onClick={() => { setAttachMode(null); setPipeError(""); setAssignError(""); }}
        sx={{ textTransform: "none", color: "#64748b" }}>
        Back
      </Button>
    )}
    <Box flex={1} />
    <Button onClick={() => { setAttachOpen(false); setAttachMode(null); setAssignDone(false); setAssignError(""); setPipeError(""); }}>
      {assignDone ? "Close" : "Cancel"}
    </Button>

    {attachMode === "direct" && (
      <Button variant="contained" onClick={async () => { await handleAddPipeline(); setAttachOpen(false); setAttachMode(null); }}
        disabled={pipeSaving || !pipeForm.job_id}
        sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}
        endIcon={pipeSaving ? <CircularProgress size={14} color="inherit" /> : null}>
        {pipeSaving ? "Adding…" : "Add to Pipeline"}
      </Button>
    )}

    {attachMode === "review" && !assignDone && (
      <Button variant="contained" onClick={handleAssign} disabled={assignSaving}
        sx={{ bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" } }}
        endIcon={assignSaving ? <CircularProgress size={16} color="inherit" /> : null}>
        {assignSaving ? "Sending…" : "Send JD to Candidate"}
      </Button>
    )}
  </DialogActions>
</Dialog>

    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// ── Main Component ────────────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════════
export default function BenchPeople() {
  // Own page padding only on the standalone /bench route.
  const { pathname } = useLocation();
  const standalone = pathname.startsWith("/bench");
  const [bench,       setBench]      = useState([]);
  const [jobs,        setJobs]       = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState("");
  const [search,      setSearch]     = useState("");
  const [statusF,     setStatusF]    = useState("");
  const [formOpen,    setFormOpen]   = useState(false);
  const [detailOpen,  setDetailOpen] = useState(false);
  const [deleteOpen,  setDeleteOpen] = useState(false);
  const [pdfOpen,     setPdfOpen]    = useState(false);
  const [selected,    setSelected]   = useState(null);
  const [formData,    setFormData]   = useState(EMPTY_FORM);
  const [saving,      setSaving]     = useState(false);
  const [addFile,     setAddFile]    = useState(null);
  const formFileRef = useRef(null);
  const [inlineFiles, setInlineFiles] = useState([]);
  const [showParsing, setShowParsing] = useState(false);
  const inlineRef = useRef(null);
  const [bulkOpen,    setBulkOpen]   = useState(false);
  const [bulkFiles,   setBulkFiles]  = useState([]);
  const [bulkStep,    setBulkStep]   = useState(0);
  const [bulkSaving,  setBulkSaving] = useState(false);
  const [bulkDone,    setBulkDone]   = useState(false);
  const [allJds,      setAllJds]     = useState([]);
  const [shareOpen,   setShareOpen]  = useState(false);
  const [shareLabel,  setShareLabel] = useState("");
  const [shareResult, setShareResult]= useState(null);
  const [shareLoading,setShareLoading]=useState(false);
  const [shareCopied, setShareCopied]= useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); setError(""); const res = await getAllBench(); setBench(res.data || []); }
    catch (err) { setError(err?.message || "Failed to load"); setBench([]); }
    finally { setLoading(false); }
  }, []);

  const loadJobs = useCallback(async () => {
    try { const res = await getAllJobs(); setJobs(res.data || []); }
    catch { setJobs([]); }
  }, []);

  // ✅ FIX 3 — errors now logged so you can see failures in console
  const loadAllJds = useCallback(async () => {
    try {
      const res = await getAllJds({ per_page: 200, is_active: "true" });
      setAllJds(res.data || []);
    } catch (err) {
      console.error("loadAllJds failed:", err);   // ← now visible in DevTools
      setAllJds([]);
    }
  }, []);

  useEffect(() => { load(); loadJobs(); loadAllJds(); }, [load, loadJobs, loadAllJds]);

  const filtered = bench.filter(p => {
    const q = search.toLowerCase();
    const skillsMatch = Array.isArray(p.skills)
      ? p.skills.some(s => (typeof s === "object" ? s.name : s).toLowerCase().includes(q))
      : false;
    const mQ = !q || p.name?.toLowerCase().includes(q) || skillsMatch || p.bench_id?.toLowerCase().includes(q);
    const mS = !statusF || p.status === statusF;
    return mQ && mS;
  });

  const stats = {
    total:       bench.length,
    available:   bench.filter(p => p.status === "Available").length,
    inInterview: bench.filter(p => p.status === "In Interview").length,
    deployed:    bench.filter(p => p.status === "Deployed").length,
  };

  const openCreate = () => { setSelected(null); setFormData(EMPTY_FORM); setAddFile(null); setFormOpen(true); };
  const openEdit   = p => { setSelected(p); setFormData({ ...EMPTY_FORM, ...p }); setAddFile(null); setFormOpen(true); };
  const openDetail = p => { setSelected(p); setDetailOpen(true); };
  const openDelete = p => { setSelected(p); setDeleteOpen(true); };
  const openPdf    = p => { setSelected(p); setPdfOpen(true); };

  const handleChange = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));


const handleSave = async (e) => {
  e.preventDefault(); setSaving(true);
  try {
    const payload = {
      ...formData,
      experience:      formData.experience      ? Number(formData.experience)      : 0,
      // current_salary:  formData.current_salary  ? Number(formData.current_salary)  : 0,
      // expected_salary: formData.expected_salary ? Number(formData.expected_salary) : 0,
    };
    if (selected) {
      const res = await updateBench(selected._id, payload);
      if (addFile) { const b64 = await toBase64(addFile); await uploadFile(selected._id, b64).catch(() => {}); }
      // ← update selected so detail dialog reflects new data immediately
      if (res?.data) setSelected(res.data);
    } else {
      const created = await createBench(payload);
      if (addFile && created?.data?._id) { const b64 = await toBase64(addFile); await uploadFile(created.data._id, b64).catch(() => {}); }
    }
    setAddFile(null); setFormOpen(false);
    await load(); // wait for list to refresh
  } catch (err) { setError(err?.message || "Save failed"); }
  finally { setSaving(false); }
};



  const handleDelete = async () => {
    try { await deleteBench(selected._id); setDeleteOpen(false); load(); }
    catch (err) { setError(err?.message || "Delete failed"); }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type === "application/pdf" || f.name.endsWith(".pdf"));
    if (!files.length) return;
    if (inlineRef.current) inlineRef.current.value = "";
    const entries = files.map(f => ({ file: f, status: "pending", file_id: "", formData: { ...EMPTY_FORM }, saved: false, errorMsg: "" }));
    setInlineFiles(entries); setShowParsing(true);
    const updated = [...entries];
    await Promise.all(entries.map(async (entry, idx) => {
      updated[idx] = { ...updated[idx], status: "parsing" };
      setInlineFiles([...updated]);
      try {
        const b64    = await toBase64(entry.file);
        const result = await parsePdf(b64, entry.file.name);
        const parsed = result.data || {};
        updated[idx] = {
          ...updated[idx], status: "done", file_id: result.file_id || "",
          formData: { ...EMPTY_FORM, ...parsed, experience: parsed.experience || "", current_salary: parsed.current_salary || "", expected_salary: parsed.expected_salary || "" },
        };
      } catch (err) {
        updated[idx] = { ...updated[idx], status: "error", file_id: err?.file_id || "", errorMsg: err?.message || "Auto-parse failed", formData: { ...EMPTY_FORM } };
      }
      setInlineFiles([...updated]);
    }));
  };

  const generateLink = (label) =>
    fetch(`${BENCH_BASE}/generate-link`, {
      method: "POST", headers: getHeaders(),
      body: JSON.stringify({ label, expires_in_days: 7 }),
    }).then(handle);

  const handleGenerateLink = async () => {
    setShareLoading(true);
    try { const res = await generateLink(shareLabel || "Candidate Registration"); setShareResult(res); }
    catch (err) { setError(err?.message || "Failed to generate link"); }
    finally { setShareLoading(false); }
  };


const handleCopyLink = async () => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(shareResult.url);
    } else {
   
      const textArea = document.createElement("textarea");
      textArea.value = shareResult.url;

      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  } catch (err) {
    console.error("Failed to copy:", err);
  }
};
  const openBulkReview = () => { setBulkFiles(inlineFiles); setBulkStep(0); setBulkDone(false); setBulkOpen(true); };
  const clearInline    = () => { setShowParsing(false); setInlineFiles([]); };

  const handleBulkChange = e => {
    const { name, value } = e.target;
    setBulkFiles(prev => prev.map((entry, idx) =>
      idx !== bulkStep ? entry : { ...entry, formData: { ...entry.formData, [name]: value } }
    ));
  };

  const handleBulkSave = async () => {
    setBulkSaving(true);
    const entry = bulkFiles[bulkStep];
    try {
      const fd = entry.formData;
      await createBench({
        ...fd,
        experience:      fd.experience      ? Number(fd.experience)      : 0,
        current_salary:  fd.current_salary  ? Number(fd.current_salary)  : 0,
        expected_salary: fd.expected_salary ? Number(fd.expected_salary) : 0,
        file_id: entry.file_id || "",
      });
      setBulkFiles(prev => prev.map((e, i) => i === bulkStep ? { ...e, saved: true } : e));
      if (bulkStep < bulkFiles.length - 1) setBulkStep(s => s + 1);
      else { setBulkDone(true); load(); }
    } catch (err) {
      setBulkFiles(prev => prev.map((e, i) => i === bulkStep ? { ...e, errorMsg: err?.message || "Save failed" } : e));
    } finally { setBulkSaving(false); }
  };

  const handleBulkSkip = () => {
    if (bulkStep < bulkFiles.length - 1) setBulkStep(s => s + 1);
    else { setBulkDone(true); load(); }
  };

  const closeBulk    = () => { setBulkOpen(false); clearInline(); load(); };
  const savedCount   = bulkFiles.filter(f => f.saved).length;
  const currentEntry = bulkFiles[bulkStep];

  if (loading) return (
    <ThemeProvider theme={recruiterTheme}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    </ThemeProvider>
  );

  return (
 <ThemeProvider theme={recruiterTheme}>
 <PageRoot $standalone={standalone}>
 <Box sx={{width:"100%",maxWidth:"100%",boxSizing:"border-box"}}>

      {error&&<Alert severity="error" onClose={()=>setError("")} sx={{borderRadius:`${RADIUS.md}px`,mb:2}}>{error}</Alert>}

      {/* ── Page Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <GradientHeading variant="h5" sx={{ fontSize: `${TYPE.page}px !important` }}>Bench People</GradientHeading>
          <Typography fontSize={TYPE.body} lineHeight={1.5} color="#8B7DB5" mt={0.5} fontFamily={FONT_BODY} fontWeight={500}>Manage internal bench resources available for deployment</Typography>
        </Box>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <Button variant="outlined" startIcon={<CloudUpload/>} onClick={()=>inlineRef.current?.click()}
            sx={{...headerBtnSx,borderColor:BRAND_DARK,color:BRAND_DARK,"&:hover":{bgcolor:"#faf5ff"}}}>
            Upload Resume
          </Button>
          <Button variant="contained" startIcon={<Add/>} onClick={openCreate}
            sx={{...headerBtnSx,bgcolor:BRAND_DARK,"&:hover":{bgcolor:BRAND_DEEP},boxShadow:"0 2px 8px rgba(79,55,139,0.35)"}}>
            Add Person
          </Button>
          <Button variant="outlined" startIcon={<Share/>} onClick={()=>setShareOpen(true)}
            sx={{...headerBtnSx,borderColor:BRAND_DARK,color:BRAND_DARK,"&:hover":{bgcolor:"#faf5ff"}}}>
            Share Form
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards ── */}
      <StatGrid>
        <StatCard title="Total Bench"  value={stats.total}       color="#6750A4" icon={<Person/>}/>
        <StatCard title="Available"    value={stats.available}   color="#6750A4" icon={<CheckCircle/>}/>
        <StatCard title="In Interview" value={stats.inInterview} color="#6750A4" icon={<Person/>}/>
        {/* <StatCard title="Deployed"     value={stats.deployed}    color="#1565C0" icon={<Done/>}/> */}
      </StatGrid>

      {/* ── Upload Zone ── */}
      <Box mb={3}>
        {!showParsing
          ?<UploadZone onFiles={handleFileSelect} fileRef={inlineRef}/>
          :<ParseProgress files={inlineFiles} onReview={openBulkReview} onClear={clearInline}/>
        }
      </Box>

      {/* ── Filters ── */}
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center"
        sx={{bgcolor:"#FAF7FF",border:"1px solid #EDE7F6",borderRadius:`${RADIUS.md}px`,p:2,mb:3}}>
        <TextField
          placeholder="Search by name, skills, or bench ID…" value={search}
          onChange={e=>setSearch(e.target.value)} size="small"
          sx={{flexGrow:1,minWidth:220,"& .MuiOutlinedInput-root":{bgcolor:"#fff",borderRadius:`${RADIUS.sm}px`}}}
          InputProps={{startAdornment:<InputAdornment position="start"><Search fontSize="small" sx={{color:"#8B7DB5"}}/></InputAdornment>,
            endAdornment:search?<InputAdornment position="end"><IconButton size="small" onClick={()=>setSearch("")}><CloseIcon fontSize="small"/></IconButton></InputAdornment>:null}}
        />
        <TextField select value={statusF} onChange={e=>setStatusF(e.target.value)} size="small" label="Status"
          sx={{minWidth:160,"& .MuiOutlinedInput-root":{bgcolor:"#fff",borderRadius:`${RADIUS.sm}px`}}}>
          <MenuItem value="">All Statuses</MenuItem>
          {BENCH_STATUSES.map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        {(search||statusF)&&<Button size="small" onClick={()=>{setSearch("");setStatusF("");}} sx={{textTransform:"none",color:"#64748b",fontWeight:600,height:32,px:2,borderRadius:`${RADIUS.sm}px`}}>Clear</Button>}
      </Box>

      {/* Table */}
      {bench.length === 0 && !error ? (
        <Card sx={{ borderRadius: `${RADIUS.md}px`, border: "1px solid #EDE7F6", boxShadow: SHADOW.card }}>
          <Box display="flex" flexDirection="column" alignItems="center" py={5} px={3} gap={1.5}>
            <Avatar sx={{ width: 72, height: 72, bgcolor: "#EDE7F6" }}>
              <Person sx={{ fontSize: 36, color: BRAND }} />
            </Avatar>
            <Typography color="text.secondary" fontSize={TYPE.cardTitle} lineHeight={1.25} fontFamily={FONT_BODY} fontWeight={700}>No bench people yet</Typography>
            <Typography fontSize={TYPE.body} lineHeight={1.5} color="text.disabled" fontFamily={FONT_BODY}>Add people or upload resumes to build your bench.</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}
              sx={{ ...headerBtnSx, mt: 0.5, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>Add Person</Button>
          </Box>
        </Card>
      ) : (
        <Card sx={{ borderRadius: `${RADIUS.md}px`, border: "1px solid #EDE7F6", boxShadow: SHADOW.card }}>
          <Paper variant="outlined" sx={{ borderRadius: `${RADIUS.md}px`, overflowX: "auto", border: "none" }}>
            <Table sx={{ tableLayout: "fixed", minWidth: 1040 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#FAF7FF" }}>
                  {BENCH_COLS.map(({ h, w }) => (
                    <TableCell key={h} align={BENCH_HEAD_ALIGN[h] || "left"} sx={{ ...benchHeadSx, width: w }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={BENCH_COLS.length} align="center" sx={{ py: 5, color: "text.secondary", fontFamily: FONT_BODY, fontSize: TYPE.body }}>
                      No bench people match your filters
                    </TableCell>
                  </TableRow>
                ) : filtered.map(p => (
                  <TableRow key={p._id} hover sx={{ "& .MuiTableCell-root": { borderBottom: "1px solid #F5F1FB", padding: "12px 16px", verticalAlign: "middle" } }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: TYPE.caption, fontWeight: 700, bgcolor: BRAND, fontFamily: FONT_BODY, flexShrink: 0 }}>
                          {nameInitials(p.name)}
                        </Avatar>
                        <Box minWidth={0} flex={1}>
                          <Typography noWrap title={p.name} fontWeight={700} fontSize={TYPE.small} fontFamily={FONT_BODY} color="#2D1B5E">{p.name}</Typography>
                          <Typography noWrap title={p.current_role || ""} fontSize={TYPE.caption} color="#8B7DB5" fontFamily={FONT_BODY}>
                            {p.bench_id}{p.current_role ? ` · ${p.current_role}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={0.5} minWidth={0}>
                        {(Array.isArray(p.skills) ? p.skills : []).slice(0, 3).map((s, i) => (
                          <Chip key={i} label={typeof s === "object" ? s.name : s} size="small" title={typeof s === "object" ? s.name : s}
                            sx={{ fontSize: TYPE.overline, height: 24, maxWidth: "100%", bgcolor: "#EDE7F6", color: BRAND, borderRadius: `${RADIUS.sm}px` }} />
                        ))}
                        {(Array.isArray(p.skills) ? p.skills : []).length > 3 && (
                          <Chip label={`+${p.skills.length - 3}`} size="small" sx={{ fontSize: TYPE.overline, height: 24, borderRadius: `${RADIUS.sm}px` }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: TYPE.small, fontFamily: FONT_BODY, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{p.experience} yrs</TableCell>
                    {/* <TableCell sx={{ fontSize: 12 }}>{fmtSalary(p.expected_salary)}</TableCell> */}
                    <TableCell sx={{ fontSize: TYPE.small, fontFamily: FONT_BODY, whiteSpace: "nowrap" }}>{fmtDate(p.availability_date)}</TableCell>
                    <TableCell sx={{ fontSize: TYPE.small, fontFamily: FONT_BODY }}><Box component="span" title={p.last_client || ""} sx={{ display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.last_client || "—"}</Box></TableCell>
                    <TableCell align="center">
                      <Chip label={p.status} color={STATUS_COLOR[p.status] || "default"} size="small" sx={{ fontWeight: 700, fontSize: TYPE.overline, height: 24, maxWidth: "100%" }} />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0} justifyContent="flex-end" sx={{ "& .MuiIconButton-root": benchIconBtnSx }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => openDetail(p)}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={p.resume_file ? "View Resume PDF" : "No resume file"}>
                          <span>
                            <IconButton size="small" onClick={() => p.resume_file && openPdf(p)}
                              sx={{ color: p.resume_file ? "#c62828" : "#bdbdbd", cursor: p.resume_file ? "pointer" : "not-allowed" }}>
                              <PictureAsPdf fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(p)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => openDelete(p)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Card>
      )}

      {/* PDF Viewer */}
      <PdfViewer open={pdfOpen} onClose={() => setPdfOpen(false)} person={selected} />

      {/* Bulk Review Dialog */}
      <Dialog open={bulkOpen} onClose={closeBulk} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: "80vh", borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #EDE7F6" }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <CloudUpload color="primary" />
            <Box>
              <Typography fontWeight={700} fontSize={TYPE.section} lineHeight={1.25} fontFamily={FONT_DISPLAY} color={BRAND_DEEP}>Review &amp; Save Bench People</Typography>
              <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>AI-extracted details pre-filled · review and confirm each</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {!bulkDone && currentEntry && (
            <Box>
              <Box sx={{ borderBottom: "1px solid #EDE7F6", px: 3, pt: 2 }}>
                <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                  {bulkFiles.map((entry, i) => (
                    <Chip key={i} label={`${i + 1}. ${entry.file.name.replace(".pdf", "").slice(0, 22)}`}
                      onClick={() => setBulkStep(i)}
                      color={entry.saved ? "success" : i === bulkStep ? "primary" : "default"}
                      variant={i === bulkStep ? "filled" : "outlined"} size="small"
                      icon={entry.saved ? <CheckCircle fontSize="small" /> : undefined}
                      sx={{ cursor: "pointer", fontWeight: i === bulkStep ? 700 : 400 }} />
                  ))}
                </Box>
                <Typography fontSize={12} color="text.secondary" pb={1.5} fontFamily={FONT_BODY}>
                  {savedCount} of {bulkFiles.length} saved · Reviewing: <strong>{currentEntry.file.name}</strong>
                </Typography>
              </Box>
              <Box p={3}>
                {currentEntry.saved ? (
                  <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
                    <CheckCircle color="success" sx={{ fontSize: 56 }} />
                    <Typography fontWeight={700} color="success.main" fontFamily={FONT_DISPLAY}>Saved!</Typography>
                    {bulkStep < bulkFiles.length - 1 && <Button variant="outlined" onClick={() => setBulkStep(s => s + 1)}>Next →</Button>}
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" required label="Full Name"        name="name"         value={currentEntry.formData.name}         onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" required type="email" label="Email" name="email"      value={currentEntry.formData.email}        onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone"                    name="phone"        value={currentEntry.formData.phone}        onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Location"                 name="location"     value={currentEntry.formData.location}     onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Current Role"             name="current_role" value={currentEntry.formData.current_role} onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Experience (yrs)" name="experience" value={currentEntry.formData.experience} onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12}>
                        <Typography fontSize={12} color="text.secondary" fontWeight={600} mb={0.8} fontFamily={FONT_BODY}>Skills &amp; Proficiency</Typography>
                        <SkillRatingInput
                          value={currentEntry.formData.skills}
                          onChange={val => setBulkFiles(prev => prev.map((entry, idx) =>
                            idx !== bulkStep ? entry : { ...entry, formData: { ...entry.formData, skills: val } }
                          ))}
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Current Salary (₹)"  name="current_salary"  value={currentEntry.formData.current_salary}  onChange={handleBulkChange} /></Grid> */}
                      {/* <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Expected Salary (₹)" name="expected_salary" value={currentEntry.formData.expected_salary} onChange={handleBulkChange} /></Grid> */}
                      <Grid item xs={12} sm={4}>
                        <TextField select fullWidth size="small" label="Notice Period" name="notice_period" value={currentEntry.formData.notice_period} onChange={handleBulkChange}>
                          {NOTICES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Last Client"  name="last_client"  value={currentEntry.formData.last_client}  onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Last Project" name="last_project" value={currentEntry.formData.last_project} onChange={handleBulkChange} /></Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField select fullWidth size="small" label="Status" name="status" value={currentEntry.formData.status} onChange={handleBulkChange}>
                          {BENCH_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Added By (Recruiter)" name="added_by" value={currentEntry.formData.added_by} onChange={handleBulkChange} /></Grid>
                    </Grid>
                    {currentEntry.errorMsg && <Alert severity="warning" sx={{ mt: 2 }}>{currentEntry.errorMsg}</Alert>}
                  </>
                )}
              </Box>
            </Box>
          )}
          {bulkDone && (
            <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: "#e8f5e9" }}><Done sx={{ fontSize: 48, color: "#2e7d32" }} /></Avatar>
              <Typography variant="h5" fontWeight={700} color="success.main" fontFamily={FONT_DISPLAY}>All Done!</Typography>
              <Typography color="text.secondary" fontFamily={FONT_BODY}>{savedCount} of {bulkFiles.length} bench people saved.</Typography>
              <Button variant="contained" onClick={closeBulk} sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND }}>Back to Bench</Button>
            </Box>
          )}
        </DialogContent>
        {!bulkDone && currentEntry && !currentEntry.saved && (
          <DialogActions sx={{ px: 3, pb: 2.5, borderTop: "1px solid #EDE7F6", gap: 1 }}>
            <Button disabled={bulkStep === 0} onClick={() => setBulkStep(s => s - 1)} startIcon={<NavigateBefore />}>Previous</Button>
            <Box flex={1} />
            <Button onClick={handleBulkSkip} color="inherit">Skip</Button>
            <Button variant="contained" onClick={handleBulkSave}
              disabled={bulkSaving || !currentEntry.formData.name || !currentEntry.formData.email}
              endIcon={bulkSaving ? <CircularProgress size={16} color="inherit" /> : <Done />}
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>
              {bulkSaving ? "Saving…" : "Save & Next"}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #EDE7F6", fontFamily: FONT_DISPLAY, color: BRAND_DEEP }}>
          {selected ? "Edit Bench Person" : "Add Bench Person"}
        </DialogTitle>
        <form onSubmit={handleSave}>
          <DialogContent sx={{ pt: 3 }}>
            <SectionLabel variant="subtitle2" mb={1.5} fontSize={13}>Personal Info</SectionLabel>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" required label="Full Name" name="name"  value={formData.name}  onChange={handleChange} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" required type="email" label="Email" name="email" value={formData.email} onChange={handleChange} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone"    name="phone"    value={formData.phone}    onChange={handleChange} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Location" name="location" value={formData.location} onChange={handleChange} /></Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <SectionLabel variant="subtitle2" mb={1.5} fontSize={13}>Professional Details</SectionLabel>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Current Role / Designation" name="current_role"     value={formData.current_role}     onChange={handleChange} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Experience (years)" name="experience" value={formData.experience}        onChange={handleChange} inputProps={{ min: 0 }} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Employment Type" name="employment_type" value={formData.employment_type} onChange={handleChange}>
                  {EMPLOYMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Added By (Recruiter)" name="added_by" value={formData.added_by} onChange={handleChange} /></Grid>
              <Grid item xs={12}>
                <Typography fontSize={12} color="text.secondary" fontWeight={600} mb={0.8} fontFamily={FONT_BODY}>Skills &amp; Proficiency</Typography>
                <SkillRatingInput value={formData.skills} onChange={val => setFormData(p => ({ ...p, skills: val }))} />
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Last Client"  name="last_client"  value={formData.last_client}  onChange={handleChange} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Last Project" name="last_project" value={formData.last_project} onChange={handleChange} /></Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <SectionLabel variant="subtitle2" mb={1.5} fontSize={13}>Compensation &amp; Availability</SectionLabel>
            <Grid container spacing={2} mb={2}>
              {/* <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Current Salary (₹)"  name="current_salary"  value={formData.current_salary}  onChange={handleChange} /></Grid> */}
              {/* <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Expected Salary (₹)" name="expected_salary" value={formData.expected_salary} onChange={handleChange} /></Grid> */}
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth size="small" label="Notice Period" name="notice_period" value={formData.notice_period} onChange={handleChange}>
                  {NOTICES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Status" name="status" value={formData.status} onChange={handleChange}>
                  {BENCH_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <TextField fullWidth multiline rows={3} size="small" label="Notes" name="notes" value={formData.notes} onChange={handleChange} sx={{ mb: 2 }} />
            <Divider sx={{ my: 2 }} />
            <SectionLabel variant="subtitle2" mb={1.5} fontSize={13}>Resume File (PDF)</SectionLabel>
            <Box onClick={() => formFileRef.current?.click()} sx={{
              border: addFile ? "2px solid #2e7d32" : "2px dashed #C4B0E8", borderRadius: 2, p: 2,
              display: "flex", alignItems: "center", gap: 2, cursor: "pointer",
              bgcolor: addFile ? "#f1f8e9" : "#F8F4FF", transition: "all 0.2s",
              "&:hover": { bgcolor: addFile ? "#e8f5e9" : "#EDE7F6" },
            }}>
              <PictureAsPdf sx={{ fontSize: 32, color: addFile ? "#2e7d32" : "#C4B0E8", flexShrink: 0 }} />
              <Box flex={1}>
                {addFile
                  ? <><Typography fontWeight={700} fontSize={13} color="success.dark" fontFamily={FONT_BODY}>{addFile.name}</Typography><Typography fontSize={11} color="text.secondary" fontFamily={FONT_BODY}>{(addFile.size / 1024).toFixed(0)} KB</Typography></>
                  : <><Typography fontWeight={600} fontSize={13} color={BRAND} fontFamily={FONT_BODY}>{selected?.resume_file ? "Replace resume PDF" : "Attach resume PDF (optional)"}</Typography><Typography fontSize={11} color="text.secondary" fontFamily={FONT_BODY}>Click to browse · PDF only</Typography></>
                }
              </Box>
              {addFile && <IconButton size="small" onClick={e => { e.stopPropagation(); setAddFile(null); }}><CloseIcon fontSize="small" /></IconButton>}
              <input ref={formFileRef} type="file" accept=".pdf,application/pdf" hidden
                onChange={e => { const f = e.target.files?.[0]; if (f) setAddFile(f); e.target.value = ""; }} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, borderTop: "1px solid #EDE7F6" }}>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>
              {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              {selected ? "Update" : "Add Person"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { minHeight: "70vh", borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #EDE7F6", fontFamily: FONT_DISPLAY, color: BRAND_DEEP }}>Bench Person Details</DialogTitle>
        {selected && (
          <BenchDetailContent
            person={selected}
            jobs={jobs}
            allJds={allJds}
            onClose={() => setDetailOpen(false)}
            onEdit={() => { setDetailOpen(false); openEdit(selected); }}
            onViewPdf={() => { setDetailOpen(false); openPdf(selected); }}
          />
        )}
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle fontWeight={700} fontFamily={FONT_DISPLAY} color={BRAND_DEEP}>Remove from Bench</DialogTitle>
        <DialogContent>
          <Typography fontFamily={FONT_BODY}>Are you sure you want to remove <strong>{selected?.name}</strong> from bench?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Share Form Dialog */}
      <Dialog open={shareOpen} onClose={() => { setShareOpen(false); setShareResult(null); setShareLabel(""); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #EDE7F6", fontFamily: FONT_DISPLAY, color: BRAND_DEEP }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Share sx={{ color: BRAND }} />
            <Typography fontWeight={700} fontFamily={FONT_DISPLAY} color={BRAND_DEEP}>Share Candidate Form</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {!shareResult ? (
            <Box display="flex" flexDirection="column" gap={2.5}>
              <Typography fontSize={13} color="text.secondary" fontFamily={FONT_BODY}>
                Generate a link to share with candidates. They fill the same form and their
                profile goes into <strong>Pending Review</strong> for you to approve.
              </Typography>
              <TextField fullWidth size="small" label="Form Label (optional)"
                placeholder="e.g. Walk-in Drive June 2026" value={shareLabel}
                onChange={e => setShareLabel(e.target.value)}
                helperText="Helps you identify which drive this link was for" />
              <Box p={2} bgcolor="#F8F4FF" borderRadius={2} sx={{ border: "1px solid #C4B0E8" }}>
                <Typography fontSize={12} color={BRAND} fontWeight={600} fontFamily={FONT_BODY}>
                  Link expires in 7 days · Multiple candidates can use the same link
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2.5}>
              <Box display="flex" alignItems="center" gap={1.5} p={2}
                bgcolor="#f0fdf4" borderRadius={2} sx={{ border: "1px solid #bbf7d0" }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography fontWeight={700} color="success.dark" fontSize={13} fontFamily={FONT_BODY}>Link generated!</Typography>
                  <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>
                    Expires: {new Date(shareResult.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography fontSize={11} fontWeight={700} color="#8B7DB5" textTransform="uppercase" mb={0.8} fontFamily={FONT_BODY} letterSpacing="0.06em">
                  Shareable Link
                </Typography>
                <Box display="flex" gap={1} alignItems="center"
                  p={1.5} bgcolor="#FAF7FF" borderRadius={2} sx={{ border: "1px solid #EDE7F6" }}>
                  <Typography fontSize={12} flex={1} sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                    {shareResult.url}
                  </Typography>
                  <Button size="small" variant="contained" onClick={handleCopyLink}
                    startIcon={shareCopied ? <CheckCircle /> : <ContentCopy />}
                    sx={{ flexShrink: 0, textTransform: "none", fontWeight: 700, bgcolor: shareCopied ? "#15803d" : BRAND,
                      "&:hover": { bgcolor: shareCopied ? "#15803d" : BRAND_DARK } }}>
                    {shareCopied ? "Copied!" : "Copy"}
                  </Button>
                </Box>
              </Box>
              <Typography fontSize={12} color="text.secondary" fontFamily={FONT_BODY}>
                Share this link via WhatsApp, email, or any channel. Candidates fill the form
                and their profile appears in your bench list under <strong>Pending Review</strong>.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: "1px solid #EDE7F6" }}>
          <Button onClick={() => { setShareOpen(false); setShareResult(null); setShareLabel(""); }}>Close</Button>
          {!shareResult && (
            <Button variant="contained" onClick={handleGenerateLink} disabled={shareLoading}
              endIcon={shareLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND, "&:hover": { bgcolor: BRAND_DARK } }}>
              {shareLoading ? "Generating…" : "Generate Link"}
            </Button>
          )}
          {shareResult && (
            <Button variant="outlined" onClick={() => { setShareResult(null); setShareLabel(""); }}
              sx={{ textTransform: "none", fontWeight: 700, borderColor: BRAND, color: BRAND }}>
              Generate Another
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
    </PageRoot>
    </ThemeProvider>
  );
}